<script setup lang="ts">
import { AlertTriangle, CheckCircle2, Info, X } from '@lucide/vue'

import { useAppNotification, type AppNotificationTone } from '@/shared/notifications/app-notification'

const { notifications, dismiss } = useAppNotification()

function iconFor(tone: AppNotificationTone) {
  if (tone === 'success') return CheckCircle2
  if (tone === 'warning' || tone === 'error') return AlertTriangle
  return Info
}
</script>

<template>
  <Teleport to="body">
    <TransitionGroup name="app-notification" tag="div" class="app-notifications" aria-live="polite">
      <aside
        v-for="notification in notifications"
        :key="notification.id"
        class="app-notification"
        :data-tone="notification.tone"
        role="status"
      >
        <component :is="iconFor(notification.tone)" :size="20" aria-hidden="true" />
        <p>{{ notification.message }}</p>
        <button
          class="app-notification__close"
          type="button"
          aria-label="关闭通知"
          @click="dismiss(notification.id)"
        >
          <X :size="17" aria-hidden="true" />
        </button>
      </aside>
    </TransitionGroup>
  </Teleport>
</template>

<style scoped>
.app-notifications {
  position: fixed;
  z-index: var(--z-toast);
  inset-block-start: var(--space-4);
  inset-inline-end: var(--space-4);
  display: grid;
  width: min(24rem, calc(100vw - var(--space-4) * 2));
  gap: var(--space-2);
}

.app-notification {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: var(--space-3);
  padding: var(--space-3);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.app-notification[data-tone='success'] {
  border-color: var(--color-success);
}

.app-notification[data-tone='success'] > svg {
  color: var(--color-success);
}

.app-notification[data-tone='warning'] {
  border-color: var(--color-warning);
}

.app-notification[data-tone='warning'] > svg {
  color: var(--color-warning);
}

.app-notification[data-tone='error'] {
  border-color: var(--color-danger);
}

.app-notification[data-tone='error'] > svg {
  color: var(--color-danger);
}

.app-notification[data-tone='info'] > svg {
  color: var(--color-primary);
}

.app-notification p {
  min-width: 0;
  margin: 0;
  color: var(--color-text);
  font-weight: 650;
}

.app-notification__close {
  display: grid;
  width: 1.75rem;
  height: 1.75rem;
  place-items: center;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
}

.app-notification__close:hover,
.app-notification__close:focus-visible {
  background: var(--color-surface-subtle);
  color: var(--color-text);
  outline: 0;
}

.app-notification-enter-active,
.app-notification-leave-active {
  transition:
    opacity var(--motion-fast) var(--ease-standard),
    transform var(--motion-fast) var(--ease-standard);
}

.app-notification-enter-from,
.app-notification-leave-to {
  opacity: 0;
  transform: translateY(calc(-1 * var(--space-2)));
}

@media (max-width: 52rem) {
  .app-notifications {
    inset-block-start: calc(var(--header-height) + var(--space-2));
  }
}
</style>
